import React, { useEffect, Component, ErrorInfo, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import { loadableReady } from '@loadable/component';
import { logger, PerformanceMarks } from '@fiverr-private/obs';
import { getContext } from '@fiverr-private/fiverr_context';
import Entry from '/home/circleci/app/apps/skill_page_perseus/src/page/entries/main/index'; // eslint-disable-line import/no-unresolved

interface ErrorBoundaryProps {
    entryName: string;
    children: ReactNode;
}

/**
 * General error boundary handler for PerseusApp CSR.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, { hasError: boolean }> {
    static getDerivedStateFromError() {
        return { hasError: true };
    }

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    public override componentDidCatch(error: Error, _: ErrorInfo) {
        const { entryName } = this.props as ErrorBoundaryProps;

        logger.error(error, {
            entryName,
            description: `Perseus CSR rendering failed for entry: ${entryName}.\n This means that your application content will not be shown and current user would experience a broken page.`,
            context: JSON.stringify(getContext()),
        });
    }

    public override render() {
        if (this.state.hasError) {
            return null;
        }

        return this.props.children;
    }
}

/**
 * An HOC that's designed to wrap the consumer's client application, and provide some default "global" behaviour.
 */
export const PerseusApp = (props: Record<string, unknown>) => {
    useEffect(() => {
        if (typeof performance === 'object') {
            performance.mark(PerformanceMarks.MOUNTED);
        }
    }, []);

    return <Entry {...props} />;
};

/**
 * Renders the Perseus client application into a respective container.
 */
const renderApp = (containerSelector: string, initialProps: Record<string, unknown>) => {
    loadableReady(() => {
        ReactDOM.hydrate(
            <ErrorBoundary entryName="main">
                <PerseusApp {...initialProps} />
            </ErrorBoundary>,
            document.querySelector(containerSelector) as ReactDOM.Container,
            () => {
                if (typeof performance === 'object') {
                    performance.mark(PerformanceMarks.RENDERED);
                }
            }
        );
    });
};

window.renderPerseusApp = renderApp;
